import type { LengthUnit } from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import {
  Measurement,
  LineMeasurementType,
  MeasurementInfoButtonConfig,
} from '@trimble/web3d-providers';
import { createMemo, Show, type JSX } from 'solid-js';
import { calculateDistance, LengthFormatter } from '@trimble/utilities';
import {
  MeasurementProperty,
  ResultRowProps,
} from '@trimble/measurements-shared';
import type { FormattingSettings } from '@trimble/types';

export interface LineMeasurementDetailsProps {
  measurement: Measurement | undefined;
  baseLengthUnit: LengthUnit;
  formattingSettings: FormattingSettings;
  infoButtonConfig?: MeasurementInfoButtonConfig;
}

export const LineMeasurementDetails = (
  props: LineMeasurementDetailsProps
): JSX.Element | null => {
  const measurementData = createMemo((): LineMeasurementType | null => {
    if (!props.measurement) return null;
    return props.measurement as LineMeasurementType;
  });

  const distanceData = createMemo((): ResultRowProps[] | null => {
    const data = measurementData();
    if (!data) return null;

    const { startPoint, endPoint } = data;
    const distance3D = calculateDistance(startPoint, endPoint);

    return [
      {
        cells: [
          {
            label: t('measurements.length'),
            value: LengthFormatter.toDisplay({
              measuredValue: distance3D,
              currentUnits: props.baseLengthUnit,
              settings: props.formattingSettings,
              useMeasurementSettings: true,
            }),
            testId: 'line-length',
            valueTestId: 'line-length-value',
          },
        ],
        testId: 'distance-values',
      },
    ];
  });

  return (
    <Show when={distanceData()}>
      {(data) => (
        <div
          class="flex flex-col gap-3 p-3"
          data-testid="line-measurement-details"
        >
          <MeasurementProperty
            icon="tc-icon-measure"
            title={t('measurements.distance')}
            resultRows={data()}
            testId="distance-section"
            infoButtonConfig={props.infoButtonConfig}
          />
        </div>
      )}
    </Show>
  );
};
